import { Box } from '@mui/material'
import ListMaterial from '@mui/material/List'
import type { FormContextValue } from 'dynamic-uniforms'
import { connectField, FormContext, useFormContext } from 'dynamic-uniforms'
import { omit } from 'ramda'
import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { BooleanRadioInput } from './BooleanRadioField'
import { filterDOMProps } from './filterDOMProps'
import ListAddField from './ListAddField'
import ListItemField from './ListItemField'

export type ListFieldProps = {
  addIcon?: ReactNode
  itemProps?: object
  children?: ReactNode
  label?: string
  value?: unknown[]
  name: string
  onChange: (value: unknown[]) => void
  disabled?: boolean
  error?: any
  field?: any
  [key: string]: any
}

function List({
  addIcon,
  children = <ListItemField name="$" />,
  itemProps,
  label,
  value,
  name,
  ...props
}: ListFieldProps & { 'ui-params'?: any }) {
  const ctx = useFormContext()
  const uiParams = props?.['ui-params'] || {}

  const [itemsExist, setItemsExist] = useState<boolean | undefined>(
    value !== undefined ? !!value?.length : undefined
  )

  useEffect(() => {
    if (value !== undefined) setItemsExist(!!value.length)
  }, [value?.length])

  const filteredProps = omit(['addItemCta', 'removeItemCta'], filterDOMProps(props))

  // Provide nested context so child fields resolve relative to this list
  const listContext = useMemo<FormContextValue>(() => ({ ...ctx, namePrefix: name }), [ctx, name])

  return (
    <FormContext.Provider value={listContext}>
      <Box justifyItems={'right'} sx={{ width: '100%', minWidth: 0 }}>
        {!!uiParams.itemsExistCta && (
          <>
            {/** @ts-ignore This provides sufficient props for the "input" needs. */}
            <BooleanRadioInput
              data-testid={`item-exists-input-${name}`}
              required
              label={uiParams.itemsExistCta}
              onChange={(value: boolean | unknown) => {
                if (typeof value === 'boolean') {
                  props.onChange(value ? [{}] : [])
                  setItemsExist(value)
                } else {
                  setItemsExist(undefined)
                }
              }}
              value={itemsExist}
            />
          </>
        )}

        <ListMaterial
          dense
          {...filteredProps}
          sx={[filteredProps.sx, { width: '100%', minWidth: 0 }]}
        >
          {value?.map((_, itemIndex) =>
            Children.map(children, (child, childIndex) =>
              isValidElement(child)
                ? cloneElement(child as ReactElement, {
                    key: `${itemIndex}-${childIndex}`,
                    name: child.props.name?.replace('$', '' + itemIndex),
                    removeItemCta: uiParams.removeItemCta,
                    ...itemProps,
                  })
                : child
            )
          )}
        </ListMaterial>

        {(!uiParams.itemsExistCta || itemsExist !== false) && (
          <Box sx={{ mr: 2 }}>
            <ListAddField icon={addIcon} name="$" cta={uiParams.addItemCta} />
          </Box>
        )}
      </Box>
    </FormContext.Provider>
  )
}

export default connectField<ListFieldProps>(List)
